/**
 * dateFormat.js — language-aware Gregorian date formatting.
 *
 * Hebrew (he):
 *   - "long_weekday"  →  "יום חמישי, 11 ביוני 2026"  (locale: he-IL)
 *   - "short"         →  "11/06/2026"                  (DD/MM/YYYY)
 *   - "medium"        →  "11 ביוני 2026"
 *
 * English (en):
 *   - "long_weekday"  →  "Thursday, June 11, 2026"   (locale: en-US)
 *   - "short"         →  "06/11/2026"                  (MM/DD/YYYY)
 *   - "medium"        →  "June 11, 2026"
 *
 * Hebrew formatting is byte-identical to the existing app behaviour
 * (the same `toLocaleDateString("he-IL", ...)` calls live throughout
 * the codebase). English formatting is new and uses the American
 * conventions per the product spec — month name spelled out for the
 * long form, MM/DD/YYYY for the short form.
 */

/**
 * Format a Date for the active UI language.
 *
 * @param {Date} d            — the date to format (treated as local).
 * @param {"he"|"en"} lang    — UI language. Hebrew is the default.
 * @param {object} [opts]
 *   @param {"long_weekday"|"medium"|"short"} [opts.dateStyle="long_weekday"]
 * @returns {string}
 */
export function formatDateForLang(d, lang, { dateStyle = "long_weekday" } = {}) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const isEn = lang === "en";
  const locale = isEn ? "en-US" : "he-IL";
  if (dateStyle === "short") {
    // MM/DD/YYYY for English, DD/MM/YYYY for Hebrew — these are what
    // each locale's toLocaleDateString returns when called with no
    // explicit options object.
    return d.toLocaleDateString(locale);
  }
  if (dateStyle === "medium") {
    return d.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  // long_weekday (default)
  return d.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
